import React from 'react';
import { X, Package, AlertCircle, Clock } from 'lucide-react';

interface CreditPurchase {
  id: string;
  credits_amount: number;
  credits_remaining: number;
  purchase_date: string;
  expiration_date: string;
  days_remaining: number;
}

interface CreditDetailsModalProps {
  userCredits: number;
  creditDetails: CreditPurchase[];
  onClose: () => void;
  onPurchaseCredits: () => void;
}

// Add a function to format the date
function formatDate(dateString: string): string {
  if (!dateString) return 'N/A';
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}

export function CreditDetailsModal({ userCredits, creditDetails, onClose, onPurchaseCredits }: CreditDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-md my-8">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg">
          <h2 className="text-xl font-semibold font-display text-brand-secondary flex items-center gap-2">
            <Package className="w-5 h-5" />
            Your Credits
          </h2>
          <button
            onClick={onClose}
            className="text-brand-secondary hover:text-brand-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2">Total Available Credits: {userCredits}</h3>
            <p className="text-sm text-gray-600">
              Credits are consumed in the order they expire, oldest first.
            </p>
            <button
              onClick={() => {
                onClose();
                onPurchaseCredits();
              }}
              className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
            >
              Buy More Credits
            </button>
          </div>

          {creditDetails.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-md font-semibold">Credit Expiration Schedule:</h3>
              <div className="max-h-[60vh] overflow-y-auto pr-2">
                {creditDetails.map((purchase) => (
                  <div key={purchase.id} className="border rounded-lg p-3 mb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{purchase.credits_remaining}</span> of {purchase.credits_amount} credits remaining
                      </div>
                      <div className={`text-sm ${purchase.days_remaining < 30 ? 'text-red-500' : 'text-gray-500'}`}>
                        {purchase.days_remaining <= 0 ? (
                          <span className="flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Expires today</span>
                        ) : (
                          <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {purchase.days_remaining} days left</span>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Purchased: {formatDate(purchase.purchase_date)} • 
                      Expires: {formatDate(purchase.expiration_date)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500">You don't have any active credits.</p>
              <button
                onClick={() => {
                  onClose();
                  onPurchaseCredits();
                }}
                className="mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
              >
                Purchase Credits
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 